import { Botao } from '@/components/ui/button';
import { Entrada, Selecao } from '@/components/ui/input';
import {
  LIMITES,
  PRESETS,
  presetAtivo,
  temFiltroAlemDoPeriodo,
  type FiltrosRelatorio,
} from '@/features/relatorios/filtros';
import {
  PRIORIDADES,
  ROTULOS_PRIORIDADE,
  ROTULOS_TIPO,
  TIPOS_SOLICITACAO,
} from '@/features/solicitacoes/status';
import { ORIGENS, ROTULOS_ORIGEM } from '@/features/solicitacoes/origem';
import type { Especialidade, UbsResumida } from '@/features/admin/tipos';

interface Props {
  filtros: FiltrosRelatorio;
  unidades: UbsResumida[] | undefined;
  especialidades: Pick<Especialidade, 'id' | 'nome'>[] | undefined;
  aoMudar: (chave: string, valor: string) => void;
  aoDefinirPeriodo: (de: string, ate: string) => void;
  aoLimpar: () => void;
}

/**
 * Barra de filtros dos relatórios.
 *
 * O período fica primeiro e sempre visível: os demais filtros recortam o que
 * já foi recortado por ele, e um número sem período é um número sem
 * significado.
 */
export function FiltrosRelatorio({
  filtros,
  unidades,
  especialidades,
  aoMudar,
  aoDefinirPeriodo,
  aoLimpar,
}: Props) {
  const preset = presetAtivo(filtros);

  return (
    <div className="border-borda bg-cartao sem-impressao mb-5 flex flex-col gap-3 rounded-xl border p-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-wrap gap-1" role="group" aria-label="Atalhos de período">
          {PRESETS.map((atalho) => {
            const intervalo = atalho.calcular(new Date());
            return (
              <Botao
                key={atalho.chave}
                tamanho="sm"
                variante={preset === atalho.chave ? 'primario' : 'contorno'}
                aria-pressed={preset === atalho.chave}
                onClick={() => aoDefinirPeriodo(intervalo.de, intervalo.ate)}
              >
                {atalho.rotulo}
              </Botao>
            );
          })}
        </div>

        <div className="flex w-full items-end gap-2 sm:w-auto">
          <label className="flex flex-col gap-1 text-xs font-medium">
            De
            <Entrada
              type="date"
              value={filtros.de}
              max={filtros.ate}
              onChange={(evento) => aoMudar('de', evento.target.value)}
              className="h-11 w-full min-w-36 sm:h-9 sm:w-40"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs font-medium">
            Até
            <Entrada
              type="date"
              value={filtros.ate}
              min={filtros.de}
              onChange={(evento) => aoMudar('ate', evento.target.value)}
              className="h-11 w-full min-w-36 sm:h-9 sm:w-40"
            />
          </label>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Selecao
          value={filtros.ubsId}
          onChange={(evento) => aoMudar('ubs', evento.target.value)}
          aria-label="Filtrar por UBS"
          className="h-11 w-full sm:h-9 sm:w-52"
        >
          <option value="">Todas as UBS</option>
          {unidades?.map((unidade) => (
            <option key={unidade.id} value={unidade.id}>
              {unidade.nome}
            </option>
          ))}
        </Selecao>

        <Selecao
          value={filtros.especialidadeId}
          onChange={(evento) => aoMudar('especialidade', evento.target.value)}
          aria-label="Filtrar por especialidade"
          className="h-11 w-full sm:h-9 sm:w-52"
        >
          <option value="">Todas as especialidades</option>
          {especialidades?.map((especialidade) => (
            <option key={especialidade.id} value={especialidade.id}>
              {especialidade.nome}
            </option>
          ))}
        </Selecao>

        <Selecao
          value={filtros.tipo}
          onChange={(evento) => aoMudar('tipo', evento.target.value)}
          aria-label="Filtrar por tipo"
          className="h-11 w-full sm:h-9 sm:w-40"
        >
          <option value="">Todos os tipos</option>
          {TIPOS_SOLICITACAO.map((tipo) => (
            <option key={tipo} value={tipo}>
              {ROTULOS_TIPO[tipo]}
            </option>
          ))}
        </Selecao>

        <Selecao
          value={filtros.prioridade}
          onChange={(evento) => aoMudar('prioridade', evento.target.value)}
          aria-label="Filtrar por prioridade"
          className="h-11 w-full sm:h-9 sm:w-40"
        >
          <option value="">Todas as prioridades</option>
          {PRIORIDADES.map((prioridade) => (
            <option key={prioridade} value={prioridade}>
              {ROTULOS_PRIORIDADE[prioridade]}
            </option>
          ))}
        </Selecao>

        <Selecao
          value={filtros.origem}
          onChange={(evento) => aoMudar('origem', evento.target.value)}
          aria-label="Filtrar por origem"
          className="h-11 w-full sm:h-9 sm:w-48"
        >
          <option value="">Todas as origens</option>
          {ORIGENS.map((origem) => (
            <option key={origem} value={origem}>
              {ROTULOS_ORIGEM[origem]}
            </option>
          ))}
        </Selecao>

        <Selecao
          value={filtros.limite}
          onChange={(evento) => aoMudar('limite', evento.target.value)}
          aria-label="Tamanho dos rankings"
          className="h-11 w-full sm:h-9 sm:w-44"
        >
          {LIMITES.map((limite) => (
            <option key={limite} value={limite}>
              Rankings com {limite}
            </option>
          ))}
        </Selecao>

        {temFiltroAlemDoPeriodo(filtros) && (
          <Botao variante="fantasma" tamanho="sm" onClick={aoLimpar}>
            Limpar filtros
          </Botao>
        )}
      </div>
    </div>
  );
}
